/**
 * =====================================================================================
 * SERVICIO DE AUTENTICACIÓN - auth.service.js
 * =====================================================================================
 *
 * Este servicio maneja todo lo relacionado con la autenticación de usuarios:
 * - Login (inicio de sesión)
 * - Cambio de contraseña
 * - Obtener información del usuario actual
 *
 * CONCEPTOS CLAVE:
 * ----------------
 *
 * JWT (JSON Web Token):
 * ---------------------
 * Es un estándar para transmitir información de forma segura entre cliente y servidor.
 * Cuando el usuario hace login, el backend genera un JWT que contiene:
 * - Identificador del usuario
 * - Roles/permisos
 * - Fecha de expiración
 *
 * Este token se guarda en localStorage y se envía en cada petición para
 * demostrar que el usuario está autenticado (ver api.js interceptores).
 *
 * FLUJO DE AUTENTICACIÓN:
 * -----------------------
 *
 *   Usuario ingresa credenciales
 *              ↓
 *   LoginView.vue llama authService.login(email, password)
 *              ↓
 *   POST /api/Auth/login → Backend valida credenciales
 *              ↓
 *   Backend responde con { token, user }
 *              ↓
 *   LoginView guarda token en localStorage
 *              ↓
 *   Usuario redirigido al Dashboard
 *              ↓
 *   Siguientes peticiones incluyen token (ver api.js)
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - api.js: Cliente HTTP que envía las peticiones
 * - src/store/modules/auth.js: Estado global de autenticación (Vuex)
 * - src/views/LoginView.vue: Formulario de login que usa este servicio
 * - src/router/index.js: Guards de navegación que verifican autenticación
 *
 * =====================================================================================
 */

// Importamos la instancia de Axios configurada en api.js
// Esta instancia ya tiene la URL base y headers configurados
import api from './api'

/**
 * =====================================================================================
 * OBJETO DEL SERVICIO DE AUTENTICACIÓN
 * =====================================================================================
 *
 * Usamos un objeto JavaScript con métodos async para organizar las funciones
 * relacionadas con autenticación. Cada método hace una petición HTTP al backend.
 *
 * ¿Por qué usar async/await?
 * --------------------------
 * Las peticiones HTTP son operaciones asíncronas (tardan tiempo).
 * async/await nos permite escribir código asíncrono de forma más legible:
 *
 * // Con async/await (más legible):
 * const response = await api.post('/Auth/login', datos)
 * return response.data
 *
 * // Sin async/await (callbacks/promesas):
 * return api.post('/Auth/login', datos).then(response => response.data)
 */
const authService = {
  /**
   * ===========================================================================
   * MÉTODO: login
   * ===========================================================================
   *
   * Autentica un usuario con email y contraseña.
   *
   * PROCESO:
   * 1. Recibe email y contraseña del formulario de login
   * 2. Envía POST /api/Auth/login con las credenciales
   * 3. Si es exitoso, el backend responde con token JWT y datos del usuario
   * 4. Retorna la respuesta al componente que llamó
   *
   * ENDPOINT DEL BACKEND: POST /api/Auth/login
   *
   * CUERPO DE LA PETICIÓN:
   * {
   *   "email": "usuario@ejemplo.com",
   *   "password": "contraseña123"
   * }
   *
   * RESPUESTA EXITOSA DEL BACKEND:
   * {
   *   "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
   *   "user": {
   *     "id": 1,
   *     "email": "usuario@ejemplo.com",
   *     "nombre": "Juan Pérez",
   *     "emisorId": 1
   *   }
   * }
   *
   * MANEJO DE ERRORES:
   * - Si las credenciales son inválidas, el backend responde 401
   * - El interceptor en api.js NO redirige al login porque es un endpoint de Auth
   * - El componente que llama debe manejar el error y mostrar mensaje al usuario
   *
   * @param {string} email - Correo electrónico del usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise<Object>} Objeto con token JWT y datos del usuario
   * @throws {Error} Si las credenciales son inválidas o hay error de red
   *
   * USO EN COMPONENTES:
   * -------------------
   * try {
   *   const { token, user } = await authService.login(email, password)
   *   localStorage.setItem('dte_token', token)
   *   localStorage.setItem('dte_user', JSON.stringify(user))
   *   router.push('/dashboard')
   * } catch (error) {
   *   mostrarError('Credenciales inválidas')
   * }
   */
  async login(email, password, ambiente) {
    // api.post() envía una petición POST al endpoint especificado
    // El segundo parámetro es el cuerpo (body) de la petición
    // Axios convierte automáticamente el objeto JavaScript a JSON
    const response = await api.post('/Auth/login', {
      email,        // Shorthand para email: email (ES6)
      password,     // Shorthand para password: password (ES6)
      ambiente      // "00"=Pruebas, "01"=Producción - actualiza Emisor.CatAmbienteDestinoId
    })

    // response.data contiene el cuerpo de la respuesta del servidor
    // En este caso: { token: "...", user: {...} }
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getCurrentUser
   * ===========================================================================
   *
   * Obtiene la información del usuario actualmente autenticado.
   *
   * ¿Cuándo se usa?
   * - Al cargar la aplicación para verificar que el token sigue siendo válido
   * - Para obtener datos actualizados del usuario después de cambios
   *
   * PROCESO:
   * 1. Envía GET /api/Auth/me con el token JWT
   * 2. El backend decodifica el token y obtiene el ID del usuario
   * 3. Consulta la base de datos y retorna los datos del usuario
   *
   * ENDPOINT DEL BACKEND: GET /api/Auth/me
   *
   * RESPUESTA EXITOSA:
   * {
   *   "id": 1,
   *   "email": "usuario@ejemplo.com",
   *   "nombre": "Juan Pérez",
   *   "emisorId": 1,
   *   "rol": "Admin"
   * }
   *
   * IMPORTANTE: Si el token expiró, el backend responde 401 y
   * el interceptor de api.js redirige automáticamente al login.
   *
   * @returns {Promise<Object>} Datos del usuario autenticado
   * @throws {Error} Si el token es inválido o expiró
   *
   * USO EN COMPONENTES:
   * -------------------
   * // En App.vue o componente de layout
   * onMounted(async () => {
   *   try {
   *     const user = await authService.getCurrentUser()
   *     store.commit('auth/SET_USER', user)
   *   } catch (error) {
   *     // Token inválido, el interceptor ya redirigió al login
   *   }
   * })
   */
  async getCurrentUser() {
    // GET request - no tiene body, solo el endpoint
    const response = await api.get('/Auth/me')
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: cambiarContexto (Solo SuperAdmin)
   * ===========================================================================
   *
   * Cambia el contexto del token JWT para SuperAdmin.
   * Esto permite que SuperAdmin trabaje con un emisor específico.
   *
   * ENDPOINT DEL BACKEND: POST /api/Auth/cambiar-contexto
   *
   * CUERPO DE LA PETICIÓN:
   * {
   *   "emisorId": 1,
   *   "sucursalId": 2 // (opcional)
   * }
   *
   * RESPUESTA EXITOSA:
   * {
   *   "token": "nuevo_jwt_token",
   *   "emisorId": 1,
   *   "emisorNombre": "Empresa ABC",
   *   "sucursalId": 2,
   *   "sucursalNombre": "Sucursal Norte"
   * }
   *
   * @param {number} emisorId - ID del emisor al que cambiar
   * @param {number|null} sucursalId - ID de la sucursal (opcional)
   * @returns {Promise<Object>} Respuesta con nuevo token y datos del contexto
   */
  async cambiarContexto(emisorId, sucursalId = null) {
    const body = { emisorId }
    if (sucursalId) {
      body.sucursalId = sucursalId
    }
    const response = await api.post('/Auth/cambiar-contexto', body)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: cambiarEmisorActivo (UsuarioCompartido + SuperAdmin)
   * ===========================================================================
   *
   * Plan B Hub-as-Emisor — Task B.2/C.1: alterna el Emisor activo del usuario
   * sin re-SSO. A diferencia de `cambiarContexto` (que sólo deja al SuperAdmin
   * leer datos de otro Emisor temporalmente), este endpoint:
   *   - Valida que el EmisorId solicitado este en el claim
   *     `emisores_accesibles` del JWT actual.
   *   - Persiste el cambio en `Usuarios.EmisorId`.
   *   - Reemite el JWT con el nuevo Emisor activo y la misma lista de
   *     accesibles (sin bumpear TokenVersion → la sesion se mantiene).
   *
   * ENDPOINT: POST /api/Auth/cambiar-emisor-activo
   *
   * BODY: { emisorId: number }
   *
   * RESPUESTAS:
   *  - 200: LoginResponseDto con el JWT reemitido
   *  - 403: { message } — Emisor fuera de `emisores_accesibles`
   *  - 404: { message } — Emisor no existe en BD
   *
   * @param {number} emisorId - ID del Emisor al que cambiar
   * @returns {Promise<Object>} LoginResponseDto (mismo shape que login)
   */
  async cambiarEmisorActivo(emisorId) {
    const response = await api.post('/Auth/cambiar-emisor-activo', { emisorId })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: refreshEmisores (Bug #3 — 2026-05-29)
   * ===========================================================================
   *
   * Plan B Hub-as-Emisor — pull fresco de `emisores_accesibles` desde SmartHub
   * via internal endpoint server-to-server. El backend reemite el JWT con la
   * lista actualizada (sin cambiar el Emisor activo ni bumpear TokenVersion).
   *
   * Cuándo usarlo: ANTES de mostrar el banner "No tenés acceso al Emisor de
   * esta clínica" en el wizard de emisión. Auto-sana sesiones cuyo claim
   * quedó stale (admin asignó/quitó Hubs después del SSO).
   *
   * ENDPOINT: POST /api/Auth/refresh-emisores
   *
   * RESPUESTAS:
   *  - 200: LoginResponseDto con JWT reemitido (mismo shape que login).
   *  - 502: SmartHub no respondió — el JWT actual sigue válido.
   *
   * @returns {Promise<Object>} LoginResponseDto reemitido
   */
  async refreshEmisores() {
    const response = await api.post('/Auth/refresh-emisores')
    return response.data
  },

  // =========================================================================
  // GOOGLE OAUTH 2.0
  // =========================================================================

  /**
   * ===========================================================================
   * MÉTODO: googleLogin
   * ===========================================================================
   *
   * Autentica un usuario con Google OAuth 2.0.
   * Si el usuario no existe, se crea automáticamente con rol EmisorAdmin.
   * Si el usuario existe, se vincula automáticamente su cuenta de Google.
   *
   * ENDPOINT DEL BACKEND: POST /api/Auth/google
   *
   * CUERPO DE LA PETICIÓN:
   * {
   *   "idToken": "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9..."
   * }
   *
   * RESPUESTA EXITOSA: LoginResponseDto (igual que login normal)
   *
   * @param {string} idToken - ID Token JWT obtenido de Google Sign-In
   * @returns {Promise<Object>} Respuesta con token JWT y datos del usuario
   */
  async googleLogin(accessToken, ambiente) {
    const response = await api.post('/Auth/google', { accessToken, ambiente })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: linkGoogleAccount
   * ===========================================================================
   *
   * Vincula una cuenta de Google al usuario autenticado actual.
   * El email de Google debe coincidir con el email del usuario.
   *
   * ENDPOINT DEL BACKEND: POST /api/Auth/link-google
   *
   * @param {string} idToken - ID Token JWT obtenido de Google Sign-In
   * @returns {Promise<Object>} { message: "Cuenta de Google vinculada exitosamente" }
   */
  async linkGoogleAccount(idToken) {
    const response = await api.post('/Auth/link-google', { idToken })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: unlinkGoogleAccount
   * ===========================================================================
   *
   * Desvincula la cuenta de Google del usuario autenticado.
   * Requiere que el usuario tenga una contraseña configurada.
   *
   * ENDPOINT DEL BACKEND: DELETE /api/Auth/unlink-google
   *
   * @returns {Promise<Object>} { message: "Cuenta de Google desvinculada exitosamente" }
   */
  async unlinkGoogleAccount() {
    const response = await api.delete('/Auth/unlink-google')
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: updateProfile
   * ===========================================================================
   *
   * Actualiza el perfil del usuario autenticado (nombre y email).
   * Usa el endpoint de usuarios con el ID del usuario actual.
   *
   * ENDPOINT DEL BACKEND: PUT /api/usuarios/{id}
   *
   * @param {number} userId - ID del usuario a actualizar
   * @param {Object} profileData - Datos del perfil { nombreCompleto, email }
   * @returns {Promise<Object>} Usuario actualizado
   */
  /**
   * Autentica un usuario a través de un código de intercambio de SmartHub (SSO).
   *
   * ENDPOINT DEL BACKEND: POST /api/Auth/hub-login
   *
   * @param {string} code - Código de intercambio generado por SmartHub
   * @param {string} ambiente - "00"=Pruebas, "01"=Producción
   * @returns {Promise<Object>} LoginResponseDto con token JWT y datos del usuario
   */
  async hubLogin(code, ambiente) {
    const response = await api.post('/Auth/hub-login', { code, ambiente })
    return response.data
  },

  async updateProfile(userId, profileData) {
    const dto = {
      NombreCompleto: profileData.nombreCompleto,
      Email: profileData.email
    }
    const response = await api.put('/auth/update-profile', dto)
    return response.data
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL SERVICIO
 * =====================================================================================
 *
 * Exportamos el objeto authService como default para que pueda ser importado
 * en otros archivos.
 *
 * USO EN OTROS ARCHIVOS:
 * ----------------------
 * // Importar el servicio completo
 * import authService from '@/services/auth.service'
 *
 * // Usar los métodos
 * await authService.login(email, password)
 * await authService.changePassword(current, nuevo)
 * await authService.getCurrentUser()
 *
 * ARCHIVOS QUE USAN ESTE SERVICIO:
 * --------------------------------
 * - src/views/LoginView.vue → Para el formulario de login
 * - src/store/modules/auth.js → Para actions de Vuex (login, logout)
 * - src/views/configuracion/*.vue → Para cambio de contraseña
 */
export default authService
